/**
 * NOM035Notifications.js
 * Sistema de notificaciones para el Responsable NOM-035
 * Muestra alertas de ATS pendientes, acciones vencidas, reevaluación próxima e IPA bajo
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bell,
  AlertTriangle,
  Clock,
  Calendar,
  TrendingDown,
  ChevronRight,
  X,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

const SEVERITY_STYLES = {
  CRITICA: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    badge: 'bg-red-600',
    text: 'text-red-800'
  },
  ALTA: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'text-orange-600',
    badge: 'bg-orange-500',
    text: 'text-orange-800'
  },
  MEDIA: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    badge: 'bg-yellow-500',
    text: 'text-yellow-800'
  }
};

const TYPE_ICONS = {
  ATS_PENDIENTE: AlertTriangle,
  ACCION_VENCIDA: Clock,
  REEVALUACION_PROXIMA: Calendar,
  IPA_BAJO: TrendingDown
};

const NOM035Notifications = ({ onNavigate }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [hasUrgent, setHasUrgent] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (user?.empresa_id) {
      loadNotifications();
      // Refresh every 5 minutes
      intervalRef.current = setInterval(loadNotifications, 5 * 60 * 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user?.empresa_id]);

  const loadNotifications = async () => {
    if (!user?.empresa_id) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${user.empresa_id}/notifications`, {
        params: { user_id: user.id }
      });
      
      if (res.data.success) {
        setNotifications(res.data.data.notifications || []);
        setUnreadCount(res.data.data.unread_count || 0);
        setCriticalCount(res.data.data.critical_count || 0);
        setHasUrgent(res.data.data.has_urgent || false);
      }
    } catch (error) {
      logger.error('Error loading NOM-035 notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await axios.post(`/v5/nom035/${user.empresa_id}/notifications/${notification.id}/read`);
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
    
    // Navigate to action URL
    if (notification.action_url && onNavigate) {
      onNavigate(notification.action_url);
    }
    
    setOpen(false);
  };

  const dismissNotification = (e, notificationId) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Don't render if no empresa or no notifications
  if (!user?.empresa_id) return null;

  const Icon = ({ type }) => {
    const IconComponent = TYPE_ICONS[type] || Bell;
    return <IconComponent className="w-5 h-5" />;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="nom035-notifications-btn"
        >
          <Bell className={`w-5 h-5 ${hasUrgent ? 'text-red-600 animate-pulse' : 'text-gray-600'}`} />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold text-white rounded-full px-1 ${criticalCount > 0 ? 'bg-red-600 animate-pulse' : 'bg-orange-500'}`}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-96 p-0 max-h-[500px] overflow-hidden" 
        align="end"
        data-testid="nom035-notifications-panel"
      >
        {/* Header */}
        <div className="p-3 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {language === 'es' ? 'Notificaciones NOM-035' : 'NOM-035 Notifications'}
            </h3>
            {unreadCount > 0 && (
              <Badge variant="secondary">
                {unreadCount} {language === 'es' ? 'nuevas' : 'new'}
              </Badge>
            )}
          </div>
          {criticalCount > 0 && (
            <p className="text-xs text-red-600 mt-1 font-medium">
              🚨 {criticalCount} {language === 'es' ? 'alertas críticas requieren atención inmediata' : 'critical alerts require immediate attention'}
            </p>
          )}
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-[400px]">
          {loading && notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto mb-2"></div>
              {language === 'es' ? 'Cargando...' : 'Loading...'}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
              <p className="text-gray-600 font-medium">
                {language === 'es' ? 'Sin notificaciones pendientes' : 'No pending notifications'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {language === 'es' ? 'Todo está bajo control' : 'Everything is under control'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const styles = SEVERITY_STYLES[notification.severity] || SEVERITY_STYLES.MEDIA;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${styles.bg} ${notification.is_urgent ? 'animate-pulse' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`flex-shrink-0 p-2 rounded-full ${styles.bg} ${styles.border} border`}>
                        <span className={styles.icon}>
                          <Icon type={notification.type} />
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Badge className={`${styles.badge} text-white text-xs mb-1`}>
                              {notification.severity}
                            </Badge>
                            <p className={`font-medium text-sm ${styles.text}`}>
                              {language === 'es' ? notification.title_es : notification.title_en}
                            </p>
                          </div>
                          <button
                            onClick={(e) => dismissNotification(e, notification.id)}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {language === 'es' ? notification.message_es : notification.message_en}
                        </p>
                        
                        {/* Action button */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </span>
                          <span className={`text-xs font-medium ${styles.text} flex items-center gap-1`}>
                            {language === 'es' ? notification.action_label_es : notification.action_label_en}
                            <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t bg-gray-50">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs text-gray-500"
              onClick={() => {
                setOpen(false);
                if (onNavigate) onNavigate('/nom035');
              }}
            >
              {language === 'es' ? 'Ver dashboard NOM-035' : 'View NOM-035 dashboard'}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NOM035Notifications;
